/** @format */

import React, { useState, useRef, useReducer, useMemo, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import ActionsCellRenderer from "./ActionsCellRenderer";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { CommoditySymbols } from "./CommoditySymbols";
// import { Chips } from 'primereact/chips';
import { Toast } from "primereact/toast";
import service from "../../utils/service";
import {
  loadCommodity,
  showDeleteConfirmationDialog,
  setSelectedCommodity,
  showAddEditDialog,
  editCommodityDetails,
  addCommodityDetails,
  setDeletedCommodity,
} from "./commodityActions";
import { newCommodity, initialCommodityState, commoditiesReducer } from "./commodityReducer";

function Commodities(props) {
  const [gridApi, setGridApi] = useState(null);
  const [state, dispatch] = useReducer(commoditiesReducer, initialCommodityState);
  const toast = useRef(null);
  const gridRef = useRef(null);
  const formRef = useRef(null);
  const columnDefs = useMemo(
    () => [
      { field: "name", headerName: "Commodity Name", filter: true },
      {
        headerName: "Commodity Symbols",
        field: "commodityDetails",
        filter: true,
        valueFormatter: (params) => params.value.map((val) => val.commoditySymbol).join(","),
        tooltipValueGetter: (params) => params.valueFormatted,
      },
      { field: "commodityType", headerName: "Vendor Source", filter: true },
      { field: "exchange", headerName: "Exchange Name", filter: true },
      { field: "reference", headerName: "Reference", filter: true },
      {
        field: "actions",
        cellRenderer: ActionsCellRenderer,
        cellRendererParams: {
          dispatch,
        },
      },
    ],
    []
  );
  const defaultColDef = useMemo(
    () => ({
      flex: 1,
    }),
    []
  );
  const getCommodity = () => {
    return service
      .getCommodoties()
      .then((res) => {
        let data = res.data.commodities.length > 0 ? res.data.commodities : null;
        dispatch(loadCommodity(data));
      })
      .catch((err) => console.error("error loading", err));
  };
  const formatDataForService = (data) => {
    const { commodityDetails, createdDate, modifiedDate, ...rest } = data;
    const commoditySymbol = commodityDetails.length !== 0 ? commodityDetails[0].commoditySymbol : "";
    const commentary = rest.commentary ?? 1;
    const forecast = rest.forecast ?? 1;
    return {
      ...rest,
      commentary,
      forecast,
      commoditySymbol,
      commodityDetailsRequestDTOS: commodityDetails.map((cd) => ({ commoditySymbol: cd.commoditySymbol })),
    };
  };
  const deleteCommodity = () => {
    const { selectedCommodity } = state;
    gridApi.showLoadingOverlay();
    service
      .deleteCommodity(selectedCommodity.commodityId)
      .then((data) => {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: `Commodity ${selectedCommodity.name} Deleted Successfully`,
          life: 10000,
        });
        return getCommodity();
      })
      .then((data) => {
        toast.current.clear();
        gridApi.hideOverlay();
      })
      .catch((err) => {
        console.error("deleteCommodity error", err.response);
        gridApi.hideOverlay();
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: `Error Deleting Commodity ${selectedCommodity.name}`,
          life: 3000,
        });
      });
  };
  const createCommodity = () => {
    const { selectedCommodity } = state;
    gridApi.showLoadingOverlay();
    service
      .createCommodity([formatDataForService(selectedCommodity)])
      .then((data) => {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: `Commodity ${selectedCommodity.name} Created`,
          life: 10000,
        });
        return getCommodity();
      })
      .then((data) => {
        toast.current.clear();
        gridApi.hideOverlay();
      })
      .catch((err) => {
        console.error("createCommodity error", err);
        gridApi.hideOverlay();
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: `Error Creating Commodity ${selectedCommodity.name}`,
          life: 3000,
        });
      });
  };
  const updateCommodity = () => {
    const { selectedCommodity } = state;
    // gridApi.doLayout();
    gridApi.showLoadingOverlay();
    service
      .updateCommodity([formatDataForService(selectedCommodity)])
      .then((data) => {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: `Commodity ${selectedCommodity.name} Updated`,
          life: 10000,
        });
        return getCommodity();
      })
      .then((data) => {
        toast.current.clear();
        gridApi.hideOverlay();
      })
      .catch((err) => {
        console.error("updateCommodity error", err);
        gridApi.hideOverlay();
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: `Error Updating Commodity ${selectedCommodity.name}`,
          life: 3000,
        });
      });
  };
  const discardChanges = () => {
    dispatch(showAddEditDialog(false));
    dispatch(showDeleteConfirmationDialog(false));
    dispatch(editCommodityDetails(false));
    dispatch(addCommodityDetails(false));
    dispatch(setDeletedCommodity([]));
    dispatch(setSelectedCommodity({}));
  };
  useEffect(() => {
    getCommodity();
  }, []);
  const onAdd = (e) => {
    dispatch(showAddEditDialog(true));
    dispatch(setSelectedCommodity(newCommodity));
    dispatch(addCommodityDetails(true));
  };
  const onGridReady = (e) => {
    e.api.showLoadingOverlay();
    setGridApi(e.api);
  };
  const getRowNodeId = (data) => data.commodityId;
  const updateSelectedCommodity = (key, value) => {
    let commodityDetails = state.selectedCommodity.commodityDetails;
    if (key === "commodityType" && value === "C3") {
      commodityDetails = commodityDetails.map((detail) => {
        if (!detail.commoditySymbol.includes("C3:")) {
          detail.commoditySymbol = "C3:" + detail.commoditySymbol;
        }
        return detail;
      });
    }
    let updatedCommodity = {
      ...state.selectedCommodity,
      commodityDetails,
      [key]: value,
    };
    dispatch(setSelectedCommodity(updatedCommodity));
  };

  return (
    <div style={{ marginTop: "1.5rem" }}>
      <Toast ref={toast} baseZIndex={20000} />
      <div
        className='ag-theme-alpine-dark'
        // style={{ height: 'calc(100vh - 80px - 1rem)', width: "100%" }}
        style={{ height: `calc(100vh - 135px - 1.5rem)`, width: "100%", marginBottom: "1rem" }}
      >
        <Button label='Add' onClick={onAdd} className='p-button p-button-sm btn3 mb-3' />
        <div className='clear'></div>
        <AgGridReact
          ref={gridRef}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={state.commodities}
          getRowNodeId={getRowNodeId}
          overlayNoRowsTemplate='<span style="color: #cccccc">No Rows to Show</span>'
          animateRows={true}
          pagination={true}
          paginationAutoPageSize={true}
          paginationPageSize={10}
          editType='fullRow'
          suppressClickEdit={true}
          onGridReady={onGridReady}
          overlayLoadingTemplate='<span class="ag-overlay-loading-center">Please wait..</span>'
          enableBrowserTooltips={true}
          tooltipShowDelay={0}
        />
      </div>
      <Dialog
        baseZIndex={10001}
        header='Discard Confirmation'
        visible={state.showDeleteConfirmDialog}
        style={{ width: "50vw" }}
        onHide={discardChanges}
        focusOnShow={false}
      >
        <p className='m-0'>Are you sure you want to delete the commodity {state.selectedCommodity.name}?</p>
        <div className='spacing1'>
          <Button label='Cancel' onClick={discardChanges} className='p-button p-button-sm btn3' />
          <Button
            label='Confirm'
            onClick={() => {
              deleteCommodity();
              dispatch(showDeleteConfirmationDialog(false));
            }}
            autoFocus
            className='p-button p-button-sm btn3'
          />
        </div>
      </Dialog>
      <Dialog
        baseZIndex={10001}
        header={state.selectedCommodity.commodityId ? "Edit Commodity" : "Add Commodity"}
        visible={state.showInsertEditDialog}
        style={{ width: "65vw" }}
        onHide={discardChanges}
        focusOnShow={false}
      >
        <form noValidate ref={formRef}>
          <div className='p-fluid formgrid grid'>
            <div className='field col-6'>
              <label htmlFor='name'>Commodity Name</label>
              <InputText
                required
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
                id='name'
                type='text'
                value={state.selectedCommodity.name || ""}
                onChange={(e) => {
                  updateSelectedCommodity(e.target.id, e.target.value);
                }}
                keyfilter={/^[a-zA-Z0-9()\-, ]+/g}
                maxLength={100}
              />
            </div>
            <div className='field col-6'>
              <label htmlFor='commodityType'>Vendor Source</label>
              <Dropdown
                required
                id='commodityType'
                options={["C3", "Future"]}
                value={state.selectedCommodity.commodityType}
                onChange={(e) => {
                  updateSelectedCommodity(e.target.id, e.target.value);
                }}
              />
            </div>
            <div className='field col-6'>
              <label htmlFor='reference'>Reference</label>
              <InputText
                required
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
                id='reference'
                type='text'
                value={state.selectedCommodity.reference || ""}
                onChange={(e) => {
                  updateSelectedCommodity(e.target.id, e.target.value);
                }}
                keyfilter={/^[a-zA-Z0-9()\-, ]+/g}
                maxLength={100}
              />
            </div>
            <div className='field col-6'>
              <label htmlFor='commodityType'>Exchange Name</label>
              <InputText
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
                id='exchange'
                type='text'
                value={state.selectedCommodity.exchange || ""}
                onChange={(e) => {
                  updateSelectedCommodity(e.target.id, e.target.value);
                }}
                keyfilter={/^[a-zA-Z0-9()\-, ]+/g}
                maxLength={100}
              />
            </div>
            <div className='field col col-12'>
              <div className='flex flex-row justify-content-between mb-1'>
                <div className='flex align-items-start'>
                  <label htmlFor='commodityDetails' className='inline-block align-self-center'>
                    Commodity Symbol
                  </label>
                  {state.selectedCommodity.commodityDetails?.length === 0 && (
                    <label className='inline-block align-self-center text-danger'> - Please add one Symbol</label>
                  )}
                </div>
                <div>
                  {!state.isAddingCommodity && (
                    <Button
                      disabled={state.isEditingCommodity}
                      onClick={(e) => {
                        dispatch(addCommodityDetails(true));
                      }}
                      icon='pi pi-plus'
                      className='commodity-action-buttons'
                    />
                  )}
                  {state.isAddingCommodity && (
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.dir(formRef.current);
                        if (!formRef.current.checkValidity()) {
                          formRef.current.reportValidity();
                        } else {
                          dispatch(addCommodityDetails(false));
                        }
                      }}
                      icon='pi pi-save'
                      className='commodity-action-buttons'
                    />
                  )}
                  {!state.isEditingCommodity && (
                    <Button
                      disabled={state.isAddingCommodity}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.dir(formRef.current);
                        if (!formRef.current.checkValidity()) {
                          formRef.current.reportValidity();
                        } else {
                          dispatch(editCommodityDetails(true));
                        }
                      }}
                      icon='pi pi-pencil'
                      className='commodity-action-buttons'
                    />
                  )}
                  {state.isEditingCommodity && (
                    <Button
                      onClick={() => {
                        dispatch(editCommodityDetails(false));
                      }}
                      icon='pi pi-save'
                      className='commodity-action-buttons'
                    />
                  )}
                </div>
              </div>
              <CommoditySymbols
                id='commodityDetails'
                value={state.selectedCommodity.commodityDetails}
                onChange={(e) => {
                  updateSelectedCommodity(e.target.id, e.target.value);
                  dispatch(addCommodityDetails(false));
                }}
                onAdd={() => {}}
                onEdit={() => {}}
                onRemove={(e) => {
                  if (e.value.length !== 0) {
                    dispatch(setDeletedCommodity([...state.deletedCommodities, e.value[0]]));
                  }
                }}
                displayLabel={"commoditySymbol"}
                isEditing={state.isEditingCommodity}
                isAdding={state.isAddingCommodity}
                selectedCommodityType={state.selectedCommodity.commodityType}
              />
              {state.deletedCommodities?.length > 0 && (
                <>
                  <label htmlFor='deleted_commodityDetails' className='inline-block align-self-center mt-3'>
                    Deleted Commodity Symbol
                  </label>
                  <CommoditySymbols
                    id='deleted_commodityDetails'
                    value={state.deletedCommodities}
                    onChange={(e) => {
                      dispatch(setDeletedCommodity(e.value));
                    }}
                    onRemove={(e) => {
                      // updateSelectedCommodity('deletedCommodity', e.value);
                      updateSelectedCommodity("commodityDetails", [
                        ...state.selectedCommodity.commodityDetails,
                        e.value[0],
                      ]);
                    }}
                    isDeleting
                    displayLabel={"commoditySymbol"}
                    selectedCommodityType={state.selectedCommodity.commodityType}
                  />
                </>
              )}
            </div>
          </div>
          <div className='spacing1'>
            <Button
              label='Cancel'
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                discardChanges();
              }}
              className='p-button p-button-sm btn3'
            />
            <Button
              label={state.selectedCommodity.commodityId ? "Update" : "Add"}
              disabled={state.selectedCommodity.commodityDetails?.length === 0}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!formRef.current.checkValidity()) {
                  formRef.current.reportValidity();
                } else {
                  if (state.selectedCommodity.commodityId) {
                    updateCommodity();
                  } else {
                    createCommodity();
                  }
                  dispatch(showAddEditDialog(false));
                }
              }}
              autoFocus
              className='p-button p-button-sm btn3'
            />
          </div>
        </form>
      </Dialog>
    </div>
  );
}

export default Commodities;
